// #36 Kpi and KpiRow, #88 KpiSelect, #59 Stat. A KPI is a tight card: the label (12 px, --text-2), the 30 px value
// with an optional unit, and a sub line (12 px --text-3, or toned ok · warn · flag). A KPI with `href` is a drill-down:
// its label is a stretched link over the card, the card lifts and the focus ring is drawn on the card (Card's rule).
// KpiSelect is a KPI that filters a list: a <button aria-pressed>, selected on --t-blue, the sub line kept --text-3
// at 12 px and never on a further tint (DC-41, F-3). Stat is the small label-over-value pair (18 px value, plan §1.3).

import type { MouseEvent, ReactNode } from "react";
import type { Blocked } from "../core";
import { Skeleton, useBlocked } from "../core";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { Tooltip } from "../lib/Tooltip";

export type KpiTone = "ok" | "warn" | "flag" | "red";

export interface KpiProps {
  /** His measure name (*Open Requisitions*, *Time to Acceptance* …). */
  label: string;
  value?: ReactNode;
  /** A unit after the value (*days*). */
  unit?: string | undefined;
  /** The sub line. */
  sub?: ReactNode;
  subTone?: KpiTone | undefined;
  /** A lead (a Ring) before the words. */
  lead?: ReactNode;
  /** The drill-down: the label becomes the card's link. */
  href?: string | undefined;
  onOpen?: ((event: MouseEvent<HTMLElement>) => void) | undefined;
  loading?: boolean | undefined;
  /** false: the seat may not see this measure; nothing renders (DC-28). */
  can?: boolean | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

function Body({ label, value, unit, sub, subTone, lead, link }: Pick<KpiProps, "value" | "unit" | "sub" | "subTone" | "lead"> & { label: ReactNode; link?: boolean }) {
  return (
    <span className="mc-kpi">
      {lead ? <span className="mc-kpi-lead">{lead}</span> : null}
      <span className={cx("mc-kx", link && "mc-kx-link")}>
        <span className="mc-kl">{label}</span>
        <span className="mc-kv2">
          {value}
          {unit ? <small>{unit}</small> : null}
        </span>
        {sub !== undefined && sub !== null ? <span className={cx("mc-ks", subTone && `mc-ks-${subTone}`)}>{sub}</span> : null}
      </span>
    </span>
  );
}

export function Kpi({ label, value, unit, sub, subTone, lead, href, onOpen, loading, can, hs, className }: KpiProps) {
  const hot = useHs(hs);
  if (can === false) return null;
  if (loading) {
    return (
      <div className={cx("mc-card", "mc-card-tight", "mc-kpi-card", className)} aria-busy="true">
        <span className="mc-kpi mc-kpi-skel">
          <Skeleton width="55%" height={12} />
          <Skeleton width="40%" height={30} />
          <Skeleton width="70%" height={12} />
        </span>
      </div>
    );
  }
  const opens = href !== undefined || onOpen !== undefined;
  const word = !opens ? label : href !== undefined
    ? <a className="mc-card-link" href={href} onClick={onOpen} {...hot}>{label}</a>
    : <button type="button" className="mc-card-link" onClick={onOpen} {...hot}>{label}</button>;
  return (
    <div className={cx("mc-card", "mc-card-tight", "mc-kpi-card", opens && "mc-lift", className)}>
      <Body label={word} value={value} unit={unit} sub={sub} subTone={subTone} lead={lead} link={opens} />
    </div>
  );
}

export interface KpiRowProps {
  children: ReactNode;
  className?: string | undefined;
}

/** The KPI row: equal tiles that reflow (two-up ≤ 768, one-up ≤ 480, and never wider than their container). */
export function KpiRow({ children, className }: KpiRowProps) {
  return <div className={cx("mc-kpis", className)}>{children}</div>;
}

export interface KpiSelectProps extends Omit<KpiProps, "href" | "onOpen" | "lead" | "subTone"> {
  pressed: boolean;
  onPressedChange?: ((pressed: boolean) => void) | undefined;
  blocked?: Blocked | undefined;
  /** "view": the tile without the filter (a plain KPI). */
  mode?: "edit" | "view" | undefined;
}

export function KpiSelect({ label, value, unit, sub, pressed, onPressedChange, loading, can, blocked, mode, hs, className }: KpiSelectProps) {
  const hot = useHs(hs);
  const block = useBlocked(blocked);
  if (can === false) return null;
  if (loading || mode === "view") return <Kpi label={label} value={value} unit={unit} sub={sub} loading={loading} className={className} />;
  const button = (
    <button
      type="button"
      className={cx("mc-kpi-sel", pressed && "mc-on", blocked && "mc-dis", className)}
      aria-pressed={pressed}
      aria-describedby={block.describedBy}
      {...block.props}
      {...hot}
      onClick={() => { if (!blocked) onPressedChange?.(!pressed); }}
    >
      <Body label={label} value={value} unit={unit} sub={sub} />
    </button>
  );
  if (!blocked) return button;
  return (
    <>
      <Tooltip label={blocked.reason} mode="mirror">{button}</Tooltip>
      {block.reason}
    </>
  );
}

export interface StatProps {
  label: string;
  value: ReactNode;
  tone?: KpiTone | undefined;
  sub?: ReactNode;
  className?: string | undefined;
}

export function Stat({ label, value, tone, sub, className }: StatProps) {
  return (
    <span className={cx("mc-stat", className)}>
      <span className="mc-sl">{label}</span>
      <span className={cx("mc-sv", tone && `mc-sv-${tone}`)}>{value}</span>
      {sub !== undefined && sub !== null ? <span className="mc-st-h">{sub}</span> : null}
    </span>
  );
}
